import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { sanitizeHexInput } from './color-role-field';
import {
  FIELD_CONTROL_FONT_SIZE,
  FIELD_CONTROL_PADDING,
  FIELD_CONTROL_RADIUS,
  fieldControlStyle,
  selectControlStyle,
  disabledSelectControlStyle,
} from './field-control-style';

/**
 * A single named option in the role dropdown.
 *
 * `value` is the string that will be persisted when the author picks this option.
 */
type ColorOption = {
  label: string;
  value: string;
};

/**
 * Props that configure a {@link CustomColorField} for a specific use-case.
 *
 * Two concrete configurations are exported below:
 *   - {@link SurfaceColorField} — heading/paragraph background
 *   - {@link ContainerTextColorField} — section text color
 */
export type CustomColorFieldConfig = {
  /**
   * Named role options to list in the dropdown, in display order.
   * The `Custom` option is appended automatically.
   */
  options: readonly ColorOption[];

  /**
   * Prefix for every `data-testid` on this widget.
   *
   * Emitted as `<prefix>-select-<name>`, `<prefix>-swatch-<name>`, `<prefix>-hex-<name>`.
   * Tests pin these strings — they must not change.
   */
  testIdPrefix: string;

  /**
   * CSS custom-property base name shared by the five design-token references this widget emits.
   *
   * The five suffixes used are:
   *   - `-select-padding`
   *   - `-border-color`
   *   - `-border-radius`
   *   - `-font-size`
   *   - `-text-color`
   *   - `-bg`
   *   - `-hex-font-family`
   */
  cssPrefix: string;

  /** Hex string seeded when the author first switches from a named role to Custom mode. */
  customDefault: string;

  /**
   * Returns `true` when the stored value maps to one of the named role options (and must
   * therefore be shown as the selected role in the dropdown, not treated as custom hex).
   *
   * The predicate differs between widgets:
   *   - `SurfaceColorField` uses `isSurfaceColorRole` (three role keys).
   *   - `ContainerTextColorField` must also treat `INHERIT_COLOR` (`'inherit'`) as a named
   *     option, so it uses a different predicate.
   */
  isNamedOption: (value: string) => boolean;

  /** Value used when `value` is nullish. */
  defaultValue: string;
};

type CustomColorFieldProps = {
  field: { label?: string };
  name: string;
  /** Either a named option value or a hex string (e.g. `'#ff0000'`). */
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const CUSTOM_OPTION_VALUE = 'custom';

const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Creates a Puck custom field component for a color control that offers a dropdown of
 * named roles plus a custom hex escape hatch.
 *
 * All interaction is parameterized via {@link CustomColorFieldConfig}: the option list,
 * `data-testid` prefix, CSS custom-property base name, the default hex seed, and the
 * predicate that decides whether a stored value is a named option or a custom hex.
 *
 * The value contract is a plain string — either a named option value or a hex literal —
 * matching both {@link SurfaceColorField} and {@link ContainerTextColorField}.
 */
export function createCustomColorField({
  options,
  testIdPrefix,
  cssPrefix,
  customDefault,
  isNamedOption,
  defaultValue,
}: CustomColorFieldConfig): (props: CustomColorFieldProps) => JSX.Element {
  const dropdownStyle: React.CSSProperties = {
    ...selectControlStyle,
    padding: `var(${cssPrefix}-select-padding, ${FIELD_CONTROL_PADDING})`,
    paddingRight: selectControlStyle.paddingRight,
    borderColor: `var(${cssPrefix}-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))`,
    borderRadius: `var(${cssPrefix}-border-radius, ${FIELD_CONTROL_RADIUS})`,
    fontSize: `var(${cssPrefix}-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
    color: `var(${cssPrefix}-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))`,
    backgroundColor: `var(${cssPrefix}-bg, var(--puck-field-color-bg, #fff))`,
  };

  return function CustomColorField({ field, name, value, onChange, readOnly }: CustomColorFieldProps): JSX.Element {
    const normalizedValue = value ?? defaultValue;
    const isCustom = !isNamedOption(normalizedValue);

    const selectValue = isCustom ? CUSTOM_OPTION_VALUE : normalizedValue;

    const handleSelectChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const chosen = e.currentTarget.value;
        if (chosen === CUSTOM_OPTION_VALUE) {
          // Only seed the starting hex when not already in custom mode.
          if (!isCustom) onChange(customDefault);
        } else {
          onChange(chosen);
        }
      },
      [isCustom, onChange]
    );

    const handleHexChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => onChange(sanitizeHexInput(e.currentTarget.value)),
      [onChange]
    );

    const labelText = field.label ?? name;

    /**
     * A hex the render path will silently ignore.
     *
     * `containerColorStyle`/`resolveSurfaceColor` emit nothing for anything failing a strict hex
     * test, so a half-typed `#f` publishes as "no override" rather than as a color. Sighted authors
     * get a weak hint (the swatch falls back to the default), but without `aria-invalid` that state
     * is conveyed by color alone and is invisible to assistive tech.
     */
    const isIncompleteHex = isCustom && !COMPLETE_HEX.test(normalizedValue);

    return (
      <FieldLabel label={labelText} readOnly={readOnly}>
        <select
          value={selectValue}
          onChange={handleSelectChange}
          disabled={readOnly}
          aria-label={labelText}
          data-testid={`${testIdPrefix}-select-${name}`}
          style={{ ...dropdownStyle, ...(readOnly && disabledSelectControlStyle) }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          <option value={CUSTOM_OPTION_VALUE}>Custom</option>
        </select>

        {isCustom && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <input
              type='color'
              value={COMPLETE_HEX.test(normalizedValue) ? normalizedValue : customDefault}
              onChange={handleHexChange}
              disabled={readOnly}
              aria-label={`${labelText} custom color swatch`}
              data-testid={`${testIdPrefix}-swatch-${name}`}
              style={{
                width: 32,
                height: 32,
                padding: 0,
                border: `1px solid var(${cssPrefix}-border-color, #d0d4d8)`,
                borderRadius: `var(${cssPrefix}-border-radius, 4px)`,
                cursor: 'pointer',
              }}
            />
            <input
              type='text'
              value={normalizedValue}
              onChange={handleHexChange}
              disabled={readOnly}
              aria-label={`${labelText} custom color value`}
              aria-invalid={isIncompleteHex || undefined}
              data-testid={`${testIdPrefix}-hex-${name}`}
              style={{
                ...fieldControlStyle,
                flex: 1,
                minWidth: 0,
                padding: `var(${cssPrefix}-select-padding, ${FIELD_CONTROL_PADDING})`,
                borderColor: `var(${cssPrefix}-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))`,
                borderRadius: `var(${cssPrefix}-border-radius, ${FIELD_CONTROL_RADIUS})`,
                fontSize: `var(${cssPrefix}-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
                fontFamily: `var(${cssPrefix}-hex-font-family, monospace)`,
              }}
            />
          </div>
        )}
      </FieldLabel>
    );
  };
}
